<template>
  <q-dialog v-model="isOpen" persistent>
    <q-card style="min-width: 600px; max-width: 800px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <q-icon name="event_note" class="q-mr-sm" />
          Meus Agendamentos
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-card-section>
        <!-- Loading State -->
        <div v-if="carregando" class="text-center q-pa-lg">
          <q-spinner size="40px" color="primary" />
          <div class="q-mt-md">Carregando agendamentos...</div>
        </div>

        <!-- Lista vazia -->
        <div v-else-if="agendamentos.length === 0" class="text-center q-pa-lg">
          <q-icon name="event_busy" size="4rem" color="grey-5" />
          <div class="text-h6 q-mt-md text-grey-7">
            Nenhum agendamento encontrado
          </div>
          <div class="text-body2 text-grey-6 q-mt-sm">
            Você ainda não possui perguntas agendadas
          </div>
        </div>

        <!-- Lista de agendamentos -->
        <div v-else class="q-gutter-md">
          <q-card
            v-for="agendamento in agendamentos"
            :key="agendamento.id"
            flat
            bordered
            class="agendamento-card"
          >
            <q-card-section>
              <div class="row items-start q-gutter-md">
                <div class="col">
                  <!-- Pergunta sempre visível -->
                  <div class="pergunta-principal q-mb-md">
                    <div class="text-h6 text-grey-8 q-mb-sm">
                      <q-icon
                        name="help_outline"
                        class="q-mr-sm"
                        color="primary"
                      />
                      {{ agendamento.pergunta }}
                    </div>
                  </div>

                  <!-- Detalhes expandíveis -->
                  <q-expansion-item
                    icon="info_outline"
                    label="Ver detalhes"
                    class="detalhes-expansion"
                    dense
                  >
                    <div class="q-pa-md q-mt-sm bg-grey-1 rounded-borders">
                      <div class="row q-gutter-md q-mb-md">
                        <div class="col-md-6 col-xs-12">
                          <div class="text-weight-medium text-grey-7 q-mb-xs">
                            <q-icon name="schedule" class="q-mr-xs" size="sm" />
                            Horário:
                          </div>
                          <div class="text-body2">
                            {{
                              agendamento.horario ||
                              "Entre 04:00 e 06:00am diariamente"
                            }}
                          </div>
                        </div>

                        <div class="col-md-6 col-xs-12">
                          <div class="text-weight-medium text-grey-7 q-mb-xs">
                            <q-icon name="event" class="q-mr-xs" size="sm" />
                            Criado em:
                          </div>
                          <div class="text-body2">
                            {{ formatarData(agendamento.timestamp) }}
                          </div>
                        </div>
                      </div>

                      <div class="row q-gutter-md q-mb-md">
                        <div class="col-md-6 col-xs-12">
                          <div class="text-weight-medium text-grey-7 q-mb-xs">
                            <q-icon name="phone" class="q-mr-xs" size="sm" />
                            Telefone:
                          </div>
                          <div class="text-body2">
                            {{ formatarTelefone(agendamento.telefone) }}
                          </div>
                        </div>

                        <div class="col-md-6 col-xs-12">
                          <div class="text-weight-medium text-grey-7 q-mb-xs">
                            <q-icon name="business" class="q-mr-xs" size="sm" />
                            Ambiente:
                          </div>
                          <div class="text-body2">
                            {{ agendamento.ambiente }}
                          </div>
                        </div>
                      </div>

                      <div class="row" v-if="agendamento._id">
                        <div class="col-12">
                          <div class="text-weight-medium text-grey-7 q-mb-xs">
                            <q-icon
                              name="fingerprint"
                              class="q-mr-xs"
                              size="sm"
                            />
                            ID do Documento:
                          </div>
                          <div
                            class="text-body2 text-grey-6"
                            style="font-family: monospace; font-size: 0.8em"
                          >
                            {{ agendamento._id }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </q-expansion-item>
                </div>

                <div class="col-auto">
                  <div class="column q-gutter-xs">
                    <q-btn
                      flat
                      round
                      color="primary"
                      icon="play_arrow"
                      size="sm"
                      @click="dispararProcessamento(agendamento)"
                      :loading="disparando === agendamento._id"
                    >
                      <q-tooltip>Disparar processamento agora</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      color="negative"
                      icon="delete"
                      size="sm"
                      @click="confirmarExclusao(agendamento)"
                      :loading="excluindo === agendamento._id"
                    >
                      <q-tooltip>Excluir agendamento</q-tooltip>
                    </q-btn>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="q-pa-md">
        <q-btn
          flat
          label="Atualizar"
          icon="refresh"
          @click="buscarAgendamentos"
          :loading="carregando"
          class="q-mr-sm"
        />
        <q-btn flat label="Fechar" v-close-popup />
      </q-card-actions>
    </q-card>

    <!-- Dialog de confirmação de exclusão -->
    <q-dialog v-model="mostrarConfirmacao" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="orange" text-color="white" />
          <span class="q-ml-sm"
            >Tem certeza que deseja excluir este agendamento?</span
          >
        </q-card-section>

        <q-card-section>
          <div class="text-body2 text-grey-7">
            <strong>Pergunta:</strong> {{ agendamentoParaExcluir?.pergunta }}
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancelar" v-close-popup />
          <q-btn
            flat
            label="Excluir"
            color="negative"
            @click="excluirAgendamento"
            :loading="excluindo"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import axios from "axios";

export default {
  name: "GestaoAgendamentosModal",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const $q = useQuasar();
    const store = useStore();
    const carregando = ref(false);
    const excluindo = ref(null);
    const disparando = ref(null);
    const agendamentos = ref([]);
    const mostrarConfirmacao = ref(false);
    const agendamentoParaExcluir = ref(null);

    const isOpen = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    // Buscar agendamentos quando o modal abre
    watch(
      () => props.modelValue,
      (novoValor) => {
        if (novoValor) {
          buscarAgendamentos();
        }
      }
    );

    const buscarAgendamentos = async () => {
      carregando.value = true;
      try {
        const usuarioLogado = store.state.autenticacao.usuarioLogado;
        const cliente = store.state.autenticacao.cliente[0];

        const response = await axios.get(
          "https://n8n.devops.multpex.com.br/webhook/89242345-d4e6-4be5-96ae-bdea27a28e51",
          {
            params: {
              ambiente: cliente,
              email: usuarioLogado.email,
            },
          }
        );

        agendamentos.value = response.data || [];
      } catch (error) {
        console.error("Erro ao buscar agendamentos:", error);
        $q.notify({
          type: "negative",
          message: "Erro ao carregar agendamentos",
          caption: "Tente novamente em alguns instantes",
        });
        agendamentos.value = [];
      } finally {
        carregando.value = false;
      }
    };

    const formatarData = (dataISO) => {
      if (!dataISO) return "Data não informada";

      try {
        const data = new Date(dataISO);
        return data.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return "Data inválida";
      }
    };

    const formatarTelefone = (telefone) => {
      if (!telefone) return "Não informado";

      // Se já está no formato 5511999999999, formatar para (11) 99999-9999
      if (telefone.length === 13 && telefone.startsWith("55")) {
        const semPais = telefone.substring(2);
        const ddd = semPais.substring(0, 2);
        const numero = semPais.substring(2);
        return `(${ddd}) ${numero.substring(0, 5)}-${numero.substring(5)}`;
      }

      return telefone;
    };

    const confirmarExclusao = (agendamento) => {
      agendamentoParaExcluir.value = agendamento;
      mostrarConfirmacao.value = true;
    };

    const excluirAgendamento = async () => {
      if (!agendamentoParaExcluir.value) return;

      excluindo.value = agendamentoParaExcluir.value.id;

      try {
        await axios.delete(
          `https://n8n.devops.multpex.com.br/webhook/06bbf97c-3af6-45a2-a8fc-ef0524143422`,
          {
            data: {
              _id: agendamentoParaExcluir.value._id,
            },
          }
        );

        $q.notify({
          type: "positive",
          message: "Agendamento excluído com sucesso!",
        });

        // Remover da lista local
        agendamentos.value = agendamentos.value.filter(
          (a) => a._id !== agendamentoParaExcluir.value._id
        );

        mostrarConfirmacao.value = false;
        agendamentoParaExcluir.value = null;
      } catch (error) {
        console.error("Erro ao excluir agendamento:", error);
        $q.notify({
          type: "negative",
          message: "Erro ao excluir agendamento",
          caption: "Tente novamente em alguns instantes",
        });
      } finally {
        excluindo.value = null;
      }
    };

    const dispararProcessamento = async (agendamento) => {
      disparando.value = agendamento._id;

      try {
        await axios.post(
          "https://n8n.devops.multpex.com.br/webhook/2f8422a2-3ad0-4d1d-b861-73507c90f0ad",
          {
            id: agendamento._id,
          }
        );

        $q.notify({
          type: "positive",
          message: "Processamento disparado com sucesso!",
          caption: "A pergunta foi enviada para processamento imediato",
        });
      } catch (error) {
        console.error("Erro ao disparar processamento:", error);
        $q.notify({
          type: "negative",
          message: "Erro ao disparar processamento",
          caption: "Tente novamente em alguns instantes",
        });
      } finally {
        disparando.value = null;
      }
    };

    return {
      isOpen,
      carregando,
      excluindo,
      disparando,
      agendamentos,
      mostrarConfirmacao,
      agendamentoParaExcluir,
      buscarAgendamentos,
      formatarData,
      formatarTelefone,
      confirmarExclusao,
      excluirAgendamento,
      dispararProcessamento,
    };
  },
};
</script>

<style scoped>
.agendamento-card {
  transition: all 0.2s ease;
}

.agendamento-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.q-card {
  border-radius: 12px;
}

.text-weight-medium {
  font-weight: 500;
}

.pergunta-principal {
  min-height: 60px;
}

.pergunta-principal .text-h6 {
  line-height: 1.4;
  word-break: break-word;
}

.detalhes-expansion {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.detalhes-expansion .q-expansion-item__content {
  padding: 0;
}

.rounded-borders {
  border-radius: 8px;
}
</style>
